import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, listOf } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RotateCcw, Trash2, Check, ListChecks, ParkingSquare } from "lucide-react";

const keyOf = (item) => `${item.type}:${item.id}`;
const toRef = (item) => ({ type: item.type, id: item.id });

const typeBadge = {
  task: { label: "Task", icon: ListChecks, cls: "bg-[#365458]/15 text-[#2A4346]" },
  idea: { label: "Idea", icon: ParkingSquare, cls: "bg-zinc-200 text-zinc-600" },
};

export default function Trash() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [pendingPurge, setPendingPurge] = useState(null); // array of items, or null
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const data = await api.getTrash();
    const merged = [
      ...data.tasks.map((t) => ({ ...t, type: "task" })),
      ...data.ideas.map((i) => ({ ...i, type: "idea" })),
    ].sort((a, b) => (b.deleted_at || "").localeCompare(a.deleted_at || ""));
    setItems(merged);
    setSelected(new Set());
  }, []);
  useEffect(() => { load(); }, [load]);

  const allSelected = items.length > 0 && selected.size === items.length;

  const toggle = (item) => {
    const next = new Set(selected);
    const k = keyOf(item);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map(keyOf)));
  };

  const selectedItems = items.filter((i) => selected.has(keyOf(i)));

  const restore = async (list) => {
    if (list.length === 0 || busy) return;
    setBusy(true);
    try {
      await api.restoreTrash(list.map(toRef));
      toast.success(list.length === 1 ? "Restored." : `Restored ${list.length} items.`);
      load();
    } finally {
      setBusy(false);
    }
  };

  const purge = async () => {
    if (!pendingPurge || busy) return;
    setBusy(true);
    try {
      await api.purgeTrash(pendingPurge.map(toRef));
      toast(pendingPurge.length === 1 ? "Gone forever." : `${pendingPurge.length} items gone forever.`);
      setPendingPurge(null);
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="Trash" subtitle="Deleted tasks and ideas. Restore them, or let them go for good.">
      {items.length === 0 ? (
        <div className="bg-zinc-800/60 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 text-sm">Trash is empty.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4 gap-2">
            <button
              data-testid="trash-select-all"
              onClick={toggleAll}
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-300 hover:text-zinc-100 px-2 py-1.5"
            >
              <span
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                  allSelected ? "bg-[#365458] border-[#365458]" : "border-zinc-500"
                }`}
              >
                {allSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
              </span>
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            {selected.size > 0 && (
              <span className="text-xs text-zinc-500">{selected.size} selected</span>
            )}
          </div>

          {selected.size > 0 && (
            <div className="flex gap-2 mb-4">
              <Button
                data-testid="trash-restore-selected"
                onClick={() => restore(selectedItems)}
                disabled={busy}
                className="flex-1 h-11 rounded-xl bg-[#365458] hover:bg-[#2A4346] text-white text-sm font-semibold"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" /> Restore
              </Button>
              <Button
                data-testid="trash-purge-selected"
                onClick={() => setPendingPurge(selectedItems)}
                disabled={busy}
                className="flex-1 h-11 rounded-xl bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-sm font-semibold"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete forever
              </Button>
            </div>
          )}

          <div className="space-y-3" data-testid="trash-list">
            {items.map((item) => {
              const k = keyOf(item);
              const isSelected = selected.has(k);
              const badge = typeBadge[item.type];
              return (
                <div
                  key={k}
                  data-testid={`trash-card-${item.id}`}
                  className={`bg-zinc-100 rounded-2xl p-4 shadow-md transition-shadow ${
                    isSelected ? "ring-2 ring-[#365458]" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      data-testid={`trash-select-${item.id}`}
                      onClick={() => toggle(item)}
                      className={`mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        isSelected ? "bg-[#365458] border-[#365458]" : "border-zinc-400 hover:border-[#365458]"
                      }`}
                    >
                      {isSelected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-900 font-semibold leading-snug">{item.title}</p>
                      {item.notes && <p className="text-sm text-zinc-500 mt-1">{item.notes}</p>}
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>
                        {listOf(item, "categories", "category").map((c) => (
                          <span key={c} className="px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600 capitalize">
                            {c}
                          </span>
                        ))}
                        {item.deleted_at && (
                          <span className="text-zinc-400">deleted {item.deleted_at.slice(0, 10)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-200">
                    <button
                      data-testid={`trash-restore-${item.id}`}
                      onClick={() => restore([item])}
                      className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium text-zinc-600 py-1.5 rounded-lg hover:bg-zinc-200"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Restore
                    </button>
                    <button
                      data-testid={`trash-purge-${item.id}`}
                      onClick={() => setPendingPurge([item])}
                      className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium text-[#991B1B] py-1.5 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete forever
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <AlertDialog open={!!pendingPurge} onOpenChange={(open) => !open && setPendingPurge(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl text-zinc-50">
              Delete forever?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {pendingPurge?.length === 1
                ? `"${pendingPurge[0].title}" will be permanently deleted. This can't be undone.`
                : `${pendingPurge?.length} items will be permanently deleted. This can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="purge-cancel"
              className="rounded-xl bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700 hover:text-zinc-100"
            >
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="purge-confirm"
              onClick={purge}
              className="rounded-xl bg-[#991B1B] hover:bg-[#7F1D1D] text-white"
            >
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
